import { useCallback, useState } from 'react';
import { MapPin, Check, Move } from 'lucide-react';
import ProvinceCitySelect from './ProvinceCitySelect';
import LocationMapPicker from './LocationMapPicker';
import { locationsApi } from '@/lib/apiServices';

/**
 * One reusable location block: province/city dropdowns, a Neshan map pin, and
 * a confirmation card with an editable address. Used by every form that needs
 * a user/vendor/admin location.
 *
 * Props:
 *   value: { location_id, latitude, longitude, address }  — controlled
 *   onChange(patch)  — receives a partial patch to merge into the form
 *   idPrefix         — keeps DOM ids unique when two pickers share a form
 */
export default function LocationPicker({
  value,
  onChange,
  idPrefix = 'location',
}) {
  const [mapCenter, setMapCenter] = useState(null);
  const [pending, setPending] = useState(null); // reverse-geocode result awaiting تأیید
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const pin =
    value.latitude != null && value.longitude != null
      ? { lat: value.latitude, lng: value.longitude }
      : null;

  const overrideCityId = pending?.city?.id ?? null;

  // Center the map on the selected city when its coordinates are known.
  const handleLocationChange = useCallback(
    async (locationId) => {
      onChange({ location_id: locationId, latitude: null, longitude: null });
      setPending(null);
      setError('');

      if (!locationId) {
        setMapCenter(null);
        return;
      }

      setMapCenter(null);
      try {
        const { data } = await locationsApi.get(locationId);
        if (data?.latitude != null && data?.longitude != null) {
          const center = { lat: data.latitude, lng: data.longitude };
          setMapCenter(center);
          // A city centre is a sensible default pin, but it is NOT the user's
          // chosen location until they confirm, so it stays pending.
          setPending({
            province: data.parent,
            city: data,
            formatted_address: data.parent ? `${data.parent.name_fa}، ${data.name_fa}` : data.name_fa,
            matched: true,
          });
        }
      } catch (err) {
        console.error(err);
        setMapCenter(null);
      }
    },
    [onChange],
  );

  const handlePinDrop = useCallback(
    async (coords) => {
      if (!coords) return;
      setError('');
      setConfirming(true);
      try {
        const { data } = await locationsApi.reverseGeocode(coords.lat, coords.lng);
        setPending(data);
        // The pin is controlled by value.latitude/longitude, so persist the
        // raw drop position immediately; location_id follows on تأیید.
        onChange({
          latitude: coords.lat,
          longitude: coords.lng,
          ...(data?.formatted_address ? { address: data.formatted_address } : {}),
        });
      } catch (err) {
        console.error(err);
        setError('دریافت موقعیت از نقشه ناموفق بود. استان و شهر را به‌صورت دستی انتخاب کنید.');
        setPending(null);
      } finally {
        setConfirming(false);
      }
    },
    [onChange],
  );

  const handleConfirm = useCallback(() => {
    if (!pending) return;
    onChange({
      location_id: pending.city?.id ?? null,
      address: pending.formatted_address ?? '',
    });
    setPending(null);
  }, [pending, onChange]);

  const handleReposition = useCallback(() => {
    setPending(null);
    onChange({ latitude: null, longitude: null, location_id: null });
  }, [onChange]);

  return (
    <div className="location-picker">
      <ProvinceCitySelect
        idPrefix={idPrefix}
        initialCityId={value.location_id}
        overrideCityId={overrideCityId}
        onChange={handleLocationChange}
      />

      <LocationMapPicker center={mapCenter} value={pin} onChange={handlePinDrop} />
      <p className="location-picker-hint">
        برای ثبت موقعیت دقیق، روی نقشه کلیک کنید، سپس آدرس پیشنهادی را بررسی و تأیید کنید.
      </p>

      {error && <div className="error-message">{error}</div>}

      {pending && (
        <div className="location-confirmation-card">
          <div className="location-confirmation-header">
            <MapPin size={16} />
            <span>موقعیت پیشنهادی</span>
            {confirming && <span className="location-confirmation-loading">در حال بررسی...</span>}
          </div>

          <dl>
            <div>
              <dt>استان</dt>
              <dd>{pending.province?.name_fa ?? 'نامشخص'}</dd>
            </div>
            <div>
              <dt>شهر</dt>
              <dd>{pending.city?.name_fa ?? 'نامشخص'}</dd>
            </div>
          </dl>

          {!pending.matched && (
            <p className="location-confirmation-warning">
              این موقعیت در فهرست شهرها پیدا نشد. استان و شهر را به‌صورت دستی انتخاب کنید.
            </p>
          )}

          <label className="location-confirmation-address">
            نشانی
            <textarea
              name={`${idPrefix}-address`}
              value={pending.formatted_address ?? ''}
              onChange={(event) =>
                setPending((current) =>
                  current ? { ...current, formatted_address: event.target.value } : current,
                )
              }
              rows={3}
            />
          </label>

          <div className="location-confirmation-actions">
            <button
              type="button"
              className="location-confirmation-confirm"
              onClick={handleConfirm}
              disabled={confirming}
            >
              <Check size={15} /> تأیید
            </button>
            <button
              type="button"
              className="location-confirmation-move"
              onClick={handleReposition}
              disabled={confirming}
            >
              <Move size={15} /> جابه‌جایی پین
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
